// Cross-screen dispatch session concerns: the auto-print preference and
// the per-user daily stats. Both were private to pages/dispatch.tsx, which
// meant the Flagged resolve route — a second entry point into the very same
// pack + Confirm Shipment surface — silently behaved differently: it
// hard-coded autoPrint={false} and rendered no stats bar.

const AUTO_PRINT_KEY = "dispatch-auto-print";

/** Auto-print the carrier label the moment Confirm Shipment mounts.
 *
 *  Defaults to OFF (per Jack, 2026-08-19). It used to default ON, which
 *  meant a desk that had never touched the setting printed labels
 *  unprompted — including on the Flagged resolve route, where a leader is
 *  usually still deciding what to ship. An operator who wants it can turn
 *  it on from the scan screens; the toggle is unchanged, only the default. */
export const getAutoPrint = (): boolean => {
    try {
        return window.localStorage.getItem(AUTO_PRINT_KEY) === "true";
    } catch { return false; }
};

export const setAutoPrintStorage = (enabled: boolean) => {
    try {
        window.localStorage.setItem(AUTO_PRINT_KEY, String(enabled));
    } catch { /* ignore */ }
};

// Stats are persisted server-side per (user, day) — the table row
// materialises on first bump and a new day implicitly starts with zeros via
// the (user_id, day) PK. No localStorage cache: a fetch on mount restores
// today's totals, and bumps fire-and-forget so the UI is optimistic but the
// DB is the source of truth.
export type DispatchStats = { itemsScanned: number; ordersShipped: number };

const currentUid = (): string | null => {
    try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return null;
        const uid = JSON.parse(raw)?.uid;
        return uid == null ? null : String(uid);
    } catch { return null; }
};

export const fetchTodayStats = async (
    apiFetch: (url: string, init?: RequestInit) => Promise<Response>,
): Promise<DispatchStats> => {
    const zero = { itemsScanned: 0, ordersShipped: 0 };
    try {
        const uid = currentUid();
        if (uid == null) return zero;
        const res = await apiFetch(`/dispatch-admin/stats?uid=${encodeURIComponent(uid)}`, {});
        if (!res.ok) return zero;
        const data = await res.json();
        return {
            itemsScanned: typeof data.itemsScanned === "number" ? data.itemsScanned : 0,
            ordersShipped: typeof data.ordersShipped === "number" ? data.ordersShipped : 0,
        };
    } catch {
        return zero;
    }
};
